import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface ToastProps {
    message: string;
    type?: 'error' | 'success' | 'info';
    isVisible: boolean;
    onClose: () => void;
    duration?: number;
}

export default function Toast({ message, type = 'info', isVisible, onClose, duration = 5000 }: ToastProps) {
    useEffect(() => {
        if (isVisible && duration > 0) {
            const timer = setTimeout(() => {
                onClose();
            }, duration);

            return () => clearTimeout(timer);
        }
    }, [isVisible, duration, onClose]);

    const colors = {
        error: 'bg-red-900/90 border-red-700/50 text-red-100',
        success: 'bg-green-900/90 border-green-700/50 text-green-100',
        info: 'bg-blue-900/90 border-blue-700/50 text-blue-100',
    };

    const icons = {
        error: '❌',
        success: '✅',
        info: 'ℹ️',
    };

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    initial={{ opacity: 0, y: -50, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -20, scale: 0.95 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                    className={`fixed top-4 right-4 z-50 p-4 rounded-lg border backdrop-blur-md shadow-2xl ${colors[type]} max-w-md`}
                >
                    <div className="flex items-start gap-3">
                        <span className="text-xl flex-shrink-0">{icons[type]}</span>
                        <div className="flex-1">
                            <p className="text-sm leading-relaxed">{message}</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="flex-shrink-0 ml-2 text-current opacity-70 hover:opacity-100 transition-opacity"
                            aria-label="Close notification"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
